import {useState} from "react";
import {Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {CancelEvent, ProlongEvent} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";

interface Props {
    event: CancelEvent | ProlongEvent;
    // Same callback that TableOverlayLayerItem fires (onClickEdit) — opens the
    // reservation in the full editor with the grouped reservations sidebar.
    onOpenReservation?: (reservation: Reservation) => void;
}

// Renders e.g. "07.05.2026 18:00" from a local date string + a "HH:mm:ss" time string.
const formatDayTime = (day: string | null, time: string | null) => {
    const date = convertLocalDateToDate(day ?? undefined);
    return [date ? formatFullDMY(date) : "", time?.slice(0, 5)].filter(Boolean).join(" ");
};

const CurrentStayInformation = ({event, onOpenReservation}: Props) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [opening, setOpening] = useState(false);
    const reservation = event._accommodationReservations[0];

    // ReservationDialog picks its specialization (accommodation / restaurant / service)
    // from the reservation's service+sector. The floorplan opens a *restaurant* one
    // (TableOverlayLayerItem.onClickEdit), so mirror that here — pass the first
    // restaurant reservation, refetched fresh by id. The dialog will show the
    // accommodation alongside it in its grouped reservations sidebar.
    const restaurantReservationId = event._restaurantReservations.find(r => !!r.id)?.id;

    const handleOpen = async () => {
        if (!onOpenReservation || !restaurantReservationId) return;

        setOpening(true);
        try {
            const fresh = await ReservationService.getReservationById(restaurantReservationId);
            if (!fresh) throw new Error("Reservation not found");
            onOpenReservation(fresh);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {
                variant: "error",
            });
        } finally {
            setOpening(false);
        }
    };

    // CancelEvent has its own departure-day fields that override the reservation
    // (the cancel scenario is "guest is leaving today", which may not be on the
    // reservation yet). ProlongEvent has no such fields, so we use the reservation.
    const bookedEndDay = "expectedDepartureDay" in event
        ? event.expectedDepartureDay ?? reservation?.expectedEndDay ?? null
        : reservation?.expectedEndDay ?? null;
    const actualEndDay = "actualDepartureDay" in event
        ? event.actualDepartureDay ?? bookedEndDay
        : reservation?.actualEndDay ?? bookedEndDay;

    // Booked = what the guest reserved. Actual = what really happened.
    // Actual values fall back to booked when the actual hasn't been recorded yet
    // (e.g. guest hasn't checked in/out so day/time fields are still empty).
    const bookedStart = formatDayTime(reservation?.expectedStartDay ?? null, reservation?.expectedStartTime ?? null);
    const bookedEnd = formatDayTime(bookedEndDay, reservation?.expectedEndTime ?? null);
    const actualStart = formatDayTime(
        reservation?.actualStartDay ?? reservation?.expectedStartDay ?? null,
        reservation?.actualStartTime ?? reservation?.expectedStartTime ?? null,
    );
    const actualEnd = formatDayTime(
        actualEndDay,
        reservation?.actualEndTime ?? reservation?.expectedEndTime ?? null,
    );

    const isCancelled = "actualDepartureDay" in event;

    const actualStartDate = convertLocalDateToDate(
        reservation?.actualStartDay ?? reservation?.expectedStartDay ?? undefined,
    );
    const actualEndDate = convertLocalDateToDate(actualEndDay ?? undefined);
    const totalActualDays = actualStartDate && actualEndDate
        ? Math.max(Math.round((actualEndDate.getTime() - actualStartDate.getTime()) / 86_400_000), 0)
        : null;

    const bookedStartDate = convertLocalDateToDate(reservation?.expectedStartDay ?? undefined);
    const bookedEndDate = convertLocalDateToDate(bookedEndDay ?? undefined);
    const totalBookedDays = bookedStartDate && bookedEndDate
        ? Math.max(Math.round((bookedEndDate.getTime() - bookedStartDate.getTime()) / 86_400_000), 0)
        : null;

    const dayDelta = actualEndDate && bookedEndDate
        ? Math.round((actualEndDate.getTime() - bookedEndDate.getTime()) / 86_400_000)
        : 0;

    return (
        <Box sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.75,
            borderLeft: "3px solid",
            borderLeftColor: "error.main",
            px: 2, py: 1.1,
            bgcolor: "background.paper",
        }}>
            <Stack gap={0.5}>
                {/* Header row — title on the left, jump-to-reservation button on the right. */}
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Typography sx={{color: "#344054", fontWeight: 700}}>
                        {t("Current Stay")}
                    </Typography>
                    {onOpenReservation && restaurantReservationId && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={opening}
                            onClick={handleOpen}
                            sx={{position: "relative"}}
                        >
                            <Box component="span" sx={{visibility: opening ? "hidden" : "visible"}}>
                                {t("Restaurant reservation")}
                            </Box>
                            {opening && (
                                <CircularProgress
                                    color="inherit"
                                    size={14}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-7px",
                                        marginLeft: "-7px",
                                    }}
                                />
                            )}
                        </Button>
                    )}
                </Box>
                {/* One line per range — clock icon + a single Typography so long text wraps
                    cleanly as text rather than breaking flex children apart. */}
                <Box sx={{display: "flex", alignItems: "flex-start", gap: 0.75}}>
                    <AccessTimeIcon sx={{fontSize: 16, color: "text.secondary", mt: "3px", flexShrink: 0}}/>
                    <Typography color="text.secondary" sx={{lineHeight: 1.5, fontWeight: 700}}>
                        {t("Actual")}
                        {" — "}
                        {t("Start")}: {actualStart || "-"}
                        {"  ·  "}
                        {t("End")}: {actualEnd || "-"}
                        {isCancelled
                            ? dayDelta ? ` (${dayDelta > 0 ? "+" : ""}${dayDelta} ${t("difference")})` : ""
                            : totalActualDays != null ? ` (${totalActualDays} ${totalActualDays === 1 ? t("day") : t("days")})` : ""}
                    </Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "flex-start", gap: 0.75}}>
                    <AccessTimeIcon sx={{fontSize: 16, color: "text.secondary", mt: "3px", flexShrink: 0}}/>
                    <Typography color="text.secondary" sx={{lineHeight: 1.5}}>
                        {t("Booked")}
                        {" — "}
                        {t("Start")}: {bookedStart || "-"}
                        {"  ·  "}
                        {t("End")}: {bookedEnd || "-"}
                        {isCancelled && totalBookedDays != null && ` (${totalBookedDays} ${totalBookedDays === 1 ? t("day") : t("days")})`}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};

export default CurrentStayInformation;
